import cn from '@/lib/clsxm';

import Container from '@/components/@base/container';
import { Text } from '@/components/@base/typography';
import XImage from '@/components/@base/x-image';
import RatingLabel from '@/components/app/product/components/title/rating-label';

import { DATA_CY_DEVELOPER_INTRODUCTION_MODAL } from '@/constant/cypress';
import { pageLevelLocalization } from '@/constant/localization';
import StoreModel from '@/models/store.model';

const { product: productLocalization } = pageLevelLocalization;

type Props = {
  store?: StoreModel;
  isVisible?: boolean;
};

export default function DeveloperIntroduction({ store, isVisible = true }: Props) {
  const animationClasses = isVisible
    ? 'opacity-100 scale-100 translate-y-0 blur-0 pointer-events-auto transition-all duration-[400ms] ease-out'
    : 'opacity-0 scale-95 translate-y-4 blur-sm pointer-events-none transition-all duration-[500ms] ease-in';

  return (
    <Container
      data-cy={DATA_CY_DEVELOPER_INTRODUCTION_MODAL}
      className={cn(
        'w-[350px] overflow-hidden rounded-md border border-[#EEF0F4] bg-white px-1.5 py-1 shadow-[0px_10px_30px_0px_rgba(0,0,0,0.08)]',
        animationClasses,
      )}
    >
      <Container className='rounded-md bg-[linear-gradient(180deg,rgba(255,182,22,0.1)_0%,rgba(255,182,22,0)_100%)] pt-4 pr-3.5'>
        <Container className='flex items-center justify-start'>
          <XImage
            src={store?.logo?.publicLink || ''}
            alt={store?.name || ''}
            width={44}
            height={44}
            className='rounded-full border-2 border-white shadow-[0px_2px_8px_0px_rgba(0,0,0,0.05)]'
          />
          <Container className='flex flex-col gap-0.5 pr-2'>
            <Text className='max-w-[250px] truncate text-[11px] font-[600]! text-[#3D3F41]'>
              {store?.name || ''}
            </Text>
            <Text className='max-w-[250px] truncate text-[11px] font-light! text-[#9D9D9D]'>
              {store?.slogan || ''}
            </Text>
          </Container>
        </Container>
      </Container>

      <Container className='flex items-center justify-center! gap-1 py-3 pr-3 pl-3 text-center text-[#424244]'>
        <Container className='flex flex-col items-center justify-center gap-0 px-1.5'>
          <Container className='text-[16px] font-bold text-[#3E4756]'>{store?.age}</Container>
          <Container className='text-[11px] font-[350]! whitespace-nowrap text-[#8A93A5]'>
            {productLocalization.membershipYear}
          </Container>
        </Container>
        {store?.totalAvgRate !== undefined &&
          store?.totalAvgRate !== null &&
          Number(store.totalAvgRate) > 0 && (
            <Container className='flex flex-col items-center justify-center gap-0'>
              <Container className='w-full border-x border-dashed border-[#E6E9EF] px-3.5 text-[16px] font-bold text-[#28A745]'>
                <RatingLabel score={store?.totalAvgRate} />
              </Container>
              <Container className='px-3.5 text-[11px] font-[350]! whitespace-nowrap text-[#8A93A5]'>
                {productLocalization.developerPerformance}
              </Container>
            </Container>
          )}
        {store?.totalProductCount !== undefined &&
          store?.totalProductCount !== null &&
          Number(store?.totalProductCount) > 0 && (
            <Container className='flex flex-col items-center justify-center gap-0 px-1.5'>
              <Container
                className={cn(
                  'text-[16px] font-bold text-[#3E4756]',
                  store?.totalAvgRate === undefined ||
                    store?.totalAvgRate === null ||
                    (Number(store.totalAvgRate) === 0 &&
                      'border-r border-dashed border-[#E6E9EF] px-12'),
                )}
              >
                {store?.totalProductCount}
              </Container>
              <Container className='text-[11px] font-[350]! whitespace-nowrap text-[#8A93A5]'>
                {productLocalization.activeProducts}
              </Container>
            </Container>
          )}
      </Container>

      <Container className='grid grid-cols-2 gap-x-2 gap-y-0 px-2'>
        {store?.medals &&
          store?.medals.length > 0 &&
          store?.medals.map((medal) => (
            <Container className='flex items-center justify-start gap-x-[3px] gap-y-0!'>
              <XImage
                src={medal?.icon?.publicLink || ''}
                alt={medal?.icon?.title || ''}
                width={23}
                height={26}
                className='shadow-[0px_2px_8px_0px_rgba(0,0,0,0.05)]'
              />
              <Text className='truncate pt-0.5 pr-0.5 text-[11px] leading-[34px] font-[350]! text-[#76767C]'>
                {medal?.title}
              </Text>
            </Container>
          ))}
      </Container>
      <Container className='flex flex-col gap-2 px-3 pb-3'></Container>
    </Container>
  );
}
